<script setup>
  import { ref, computed, watchEffect, defineAsyncComponent } from 'vue';
  import { useSpotStore } from "@/stores/SpotStore";
  import { useUserStore } from "@/stores/UserStore";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import CompleteReportDataContainerSection from '@/components/CompleteReportDataContainerSection.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import CurrentConditionsAd from './CurrentConditionsAd.vue';
  import CurrentConditionsComboContainer from './CurrentConditionsComboContainer.vue';
  
  const { $COMPLETE_REPORT_MAP } = useSettingsGlobals();

  // Normal desktop media query width is 840px, using 940 for this component only
  const isDesktopAd = useMediaQuery('(min-width: 940px)');
  const isSmallDevice = useMediaQuery('(max-width: 550px)');

  const FlowMap = defineAsyncComponent(() => import('@/components/FlowMap.vue')); 
  const CurrentConditionsGaugeContainer = defineAsyncComponent(() => import('@/components/CurrentConditionsGaugeContainer.vue')); 
  const CurrentConditionsDataContainer = defineAsyncComponent(() => import('@/components/CurrentConditionsDataContainer.vue')); 

  const props = defineProps({
    upgrade: {
      type: Array,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();

  const comboContainerOpen = ref(false);

  const flowMapAllowed = computed(() => $COMPLETE_REPORT_MAP === true);
  const showAd = computed(() => isDesktopAd.value && !flowMapAllowed.value && userStore.showAds);
  const minHeight = computed(() => {
    if (!isSmallDevice) return '300px';

    if (!flowMapAllowed.value) {
      return comboContainerOpen.value ? '294px' : '134px';
    }

    return comboContainerOpen.value ? '405px' : '246px';
  });

  function setCurrentCondtionsAdAttribute() {
    const body = document.documentElement;
    body.dataset.ccAd = true;
  }

  function handleContainerOpenChange(value) {
    comboContainerOpen.value = value;
  }

  watchEffect(() => !flowMapAllowed.value && setCurrentCondtionsAdAttribute());
</script>

<template>
    <CompleteReportDataContainerSection
      sectionId="current-conditions"
      title="'Current Conditions'"
      contentClass="jwx-complete-report__current-conditions"
      :min-height="minHeight"
      :alwaysShowMetaActions="true"
      :metaActions="false"
    >
      <template #content="{ isVisible }">
        <FlowMap
          v-if="flowMapAllowed && isVisible"
          :lat="spotStore.lat"
          :lon="spotStore.lon"
        />
        <CurrentConditionsGaugeContainer
          v-if="isVisible && !isSmallDevice"
          :upgrade="props.upgrade"
        />
        <CurrentConditionsDataContainer
          v-if="isVisible && !isSmallDevice"
          :upgrade="props.upgrade"
        />
        <CurrentConditionsComboContainer
          v-if="isVisible && isSmallDevice"
          @containerOpen:change="handleContainerOpenChange($event)"
          :upgrade="props.upgrade"
        />
        <CurrentConditionsAd
          v-if="showAd"
        />
      </template>
    </CompleteReportDataContainerSection>
</template>